import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { kpisAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Trash2, RotateCcw, AlertCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function KPITrash() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [deletedKpis, setDeletedKpis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoreDialog, setRestoreDialog] = useState(null);
  const [restoring, setRestoring] = useState(false);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!isAdmin) { navigate('/kpis'); return; }
    fetchTrash();
  }, [isAdmin, navigate]);

  const fetchTrash = async () => {
    setLoading(true);
    try {
      const res = await kpisAPI.getTrash();
      setDeletedKpis(res.data);
    } catch (error) {
      toast.error('Error al cargar papelera');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!restoreDialog) return;
    setRestoring(true);
    try {
      await kpisAPI.restore(restoreDialog.id);
      toast.success(`KPI "${restoreDialog.name}" restaurado correctamente`);
      setRestoreDialog(null);
      fetchTrash();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restaurar');
    } finally {
      setRestoring(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <Layout>
      <div className="space-y-6" data-testid="kpi-trash-page">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/kpis')} data-testid="back-to-kpis-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver a KPIs
          </Button>
        </div>

        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Trash2 className="w-6 h-6 text-red-500" />
            KPIs Eliminados
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            KPIs eliminados pueden ser restaurados manteniendo todo su historial
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-400">Cargando...</div>
        ) : deletedKpis.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Trash2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">La papelera está vacía</p>
              <p className="text-sm text-slate-400 mt-1">Los KPIs eliminados aparecerán aquí</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {deletedKpis.map((kpi) => (
              <Card key={kpi.id} className="border-red-100" data-testid={`trash-kpi-${kpi.id}`}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-slate-700 truncate">{kpi.name}</h3>
                        <Badge variant="outline" className="text-xs text-red-500 border-red-200">Eliminado</Badge>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                        <span>Area: {kpi.area}</span>
                        <span>Periodicidad: {kpi.goal_periodicity === 'quarterly' ? 'Trimestral' : 'Bimestral'}</span>
                        {kpi.deleted_at && (
                          <span>Eliminado: {new Date(kpi.deleted_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        )}
                        {kpi.deleted_by_name && <span>Por: {kpi.deleted_by_name}</span>}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRestoreDialog(kpi)}
                      className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 ml-4 flex-shrink-0"
                      data-testid={`restore-kpi-${kpi.id}`}
                    >
                      <RotateCcw className="w-4 h-4 mr-1" /> Restaurar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Restore confirmation dialog */}
        <Dialog open={!!restoreDialog} onOpenChange={(open) => { if (!open) setRestoreDialog(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Restaurar KPI</DialogTitle>
              <DialogDescription>
                Se restaurará <strong>{restoreDialog?.name}</strong>. Volverá a aparecer en todos los módulos con todo su historial preservado.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRestoreDialog(null)}>Cancelar</Button>
              <Button onClick={handleRestore} disabled={restoring} data-testid="confirm-restore-btn">
                {restoring ? 'Restaurando...' : 'Confirmar Restauración'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
